import sendboxApi from './sendboxApi';
import { orDefault } from '@/common/utils';
import readService from '@/services/readService';
import acknowledgeService from '@/services/acknowledgeService';
import favoritesService from '@/services/favoritesService';
import sendboxService from '@/services/sendboxService';
import mandatoryToReadService from '@/services/mandatoryToReadService';
import SendboxItem2 from './SendboxItem2';
import i18n from '@/plugins/i18n';
import { delay } from '@/common/utils';
import eventBus from '@/common/eventBus';

function createSendboxItem2({ subject = {}, payload, userState, action, writeAuditLog = true }) {
  return new SendboxItem2({ moduleId: subject._moduleId, payload, subject, writeAuditLog, userState, action });
}

// --------------------------------------------------------------------------------------------------------------------------------------------------------------
const state = {
  isSynchronized: true,
  sendbox: [], // shadow store for sendbox items. LocalStorage is not reactive therefore we store it in the in-memory store too
  sendboxSent: [], // shadow store for senboxSent items. LocalStorage is not reactive therefore we store it in the in-memory store too
  userActionPerformed: 0,
};

// --------------------------------------------------------------------------------------------------------------------------------------------------------------
const getters = {
  /**
   * Get all sendbox items which await being sent to the server.
   * @returns all sendbox items.
   */
  getSendbox: (state) => () => {
    let sendBoxLocalStorageItem = localStorage.getItem('sendbox');
    try {
      let sendbox = JSON.parse(sendBoxLocalStorageItem ? sendBoxLocalStorageItem : '[]');
      return sendbox;
    } catch (err) {
      // Fallback: Use store as source
      return state.sendbox;
    }
  },

  /**
   * Get all sendbox items which have been sent to the server previously.
   * @returns Array of sent SendboxItems
   */
  getSendboxSentItems: (state) => () => {
    let sendBoxLocalStorageItem = localStorage.getItem('sendbox.sent');
    try {
      return JSON.parse(sendBoxLocalStorageItem ? sendBoxLocalStorageItem : '[]');
    } catch (err) {
      // Fallback: Use store as source
      return state.sentItems;
    }
  },
};

// --------------------------------------------------------------------------------------------------------------------------------------------------------------
const mutations = {
  markAsFavorite(state, item) {
    item._userState.favorite = 1;
    favoritesService.markAsFavorite(item);
  },
  removeFromFavorite(state, item) {
    item._userState.favorite = 0;
    favoritesService.removeFromFavorites(item);
  },
  /**
   * Pushes an array of type SendboxItem2 items to the sendbox store, which is backed by local storage.
   * @param {Object} state - vuex state
   * @param {Array.<SendboxItem2>} sendboxItems one or more SendboxItems
   */
  pushSendboxItems(state, sendboxItems) {
    const sendBoxLocalStorageItem = localStorage.getItem('sendbox');
    const sendbox = JSON.parse(sendBoxLocalStorageItem ? sendBoxLocalStorageItem : '[]');

    const timestamp = Date.now();
    sendboxItems.forEach((sendboxItem) => {
      sendboxItem.objectCreated = timestamp;
      sendbox.push(sendboxItem);
    });

    localStorage.setItem('sendbox', JSON.stringify(sendbox));
    state.sendbox = sendbox;
    state.userActionPerformed++;
  },

  /**
   * An instance of SendboxItem.js is added to the sendbox (store backed by localStorage item "sendbox")
   * @param {SendboxItem} sendboxItem¶
   */
  addAction(state, action) {
    const sendBoxLocalStorageItem = localStorage.getItem('sendbox');
    const sendbox = JSON.parse(sendBoxLocalStorageItem ? sendBoxLocalStorageItem : '[]');

    if (action) {
      action.objectCreated = Date.now();
      sendbox.push(action);
    }

    localStorage.setItem('sendbox', JSON.stringify(sendbox));
    state.sendbox = sendbox;

    // console.debug('action', action, ' has been added to sendbox backed by localStorage sendbox');
  },

  /**
   * An instance of SendboxItem.js is added to the sendbox (store backed by localStorage item "sendbox")
   * @param {SendboxItem} sendboxItem
   */
  addSendboxItem(state, sendboxItem) {
    console.debug('addSendboxItem', JSON.stringify(sendboxItem, null, 2));
    const sendBoxLocalStorageItem = localStorage.getItem('sendbox');
    const sendbox = JSON.parse(sendBoxLocalStorageItem ? sendBoxLocalStorageItem : '[]');

    sendboxItem.objectCreated = Date.now();
    sendbox.push(sendboxItem);

    localStorage.setItem('sendbox', JSON.stringify(sendbox));
    state.sendbox = sendbox;

    // console.debug('sendboxItem', sendboxItem, ' has been added to sendbox backed by localStorage sendbox');
  },

  /**
   * Sets the sendbox and the sent items to the store and backed localStorage
   * @param {SendboxItem[]} sendbox the array of sendbox items currently awaiting to be posted to the server
   * @param {SendboxItem[]} sentItems the history, that is a number (configured) of items already sent to the server
   */
  setSendboxAndHistory(state, { sendbox, sentItems }) {
    try {
      const mappedItems = sentItems.map((i) => {
        return sendboxService.stripImageDataFromJson(i);
      });
      /*
      const mappedItems = sentItems.map((i) => {
        // Serialize to make a deep clone
        const itemString = JSON.stringify(i);
        console.debug('setSendboxAndHistory 0:', i);
        console.debug('setSendboxAndHistory 1:', itemString);
        // Omit base64-strings (to avoid exceeding the maximum size of localStorage items)
        let stringWithoutImages = itemString.replaceAll(/\\"data:image\/.*?"/g, '\\"images (base64 image values have been omitted)\\"');
        stringWithoutImages = stringWithoutImages.replaceAll(/"data:image\/.*?"/g, '"images (base64 image values have been omitted)"');
        console.debug('setSendboxAndHistory 2:', stringWithoutImages);
        return JSON.parse(stringWithoutImages);
      });
      */
      const stringifiedItems = JSON.stringify(mappedItems);
      localStorage.setItem('sendbox.sent', stringifiedItems);
      state.sendboxSent = sentItems;

      localStorage.setItem('sendbox', JSON.stringify(sendbox));
      state.sendbox = sendbox;
    } catch (err) {
      console.error("Failed to prepare 'sendbox and history'. Keep localStorage (that is do not processSentSendbox)", err);
      console.debug(`setSendboxAndHistory sentItems=${sentItems.length}, sendbox=${sendbox.length}`);
    }
  },
};

// --------------------------------------------------------------------------------------------------------------------------------------------------------------
const actions = {
  /**
   * Creates a SendboxItem2 object and sends it via sendbox to the server.
   * @param {string} moduleType - the moduleType defined in module index.moduleType
   * @param {string} moduleId - the moduleId defined in module index.moduleId
   * @param {Object} subject - sendbox item subject, for example a document metadata item
   * @param {Object} payload - arbitrary payload to send with
   * @param {Object} userState - the userState to send
   * @param {Object} displayTitle - the text to display in sendbox and snackbar as title
   * @param {Object} displayText - the text to display in sendbox and snackbar
   * @param {string} action - the action name, e.g. setElearningState
   * @param {boolean} showAsUserInfo - display in snackbar or the like
   * @param {boolean} showInSendbox - display sendbox
   * @param {boolean} writeAuditLog - write into the audit log table (serverside)
   */

  async markAsReadMedia({ commit, dispatch, rootState, state }, { moduleType, moduleId, item, sendboxTitle: title, snackbarText: text }) {
    mandatoryToReadService.markAsRead({ moduleId, item });
    await dispatch('performSendboxItem2Action', {
      subject: item,
      moduleType,
      moduleId,
      action: 'SET_READ_STATE',
      title,
      text,
      showInSendbox: true,
      writeAuditLog: false,
    });
  },
  async markAsAcknowledgedMedia({ commit, dispatch, rootState, state }, { moduleType, moduleId, item, sendboxTitle: title, snackbarText: text }) {
    acknowledgeService.markAsAcknowledged(item);
    await dispatch('performSendboxItem2Action', {
      subject: item,
      moduleType,
      moduleId,
      action: 'SET_ACKNOWLEDGED',
      title,
      text,
      showInSendbox: true,
      writeAuditLog: false,
    });
  },
  async markAsFavoriteMedia({ commit, dispatch, rootState, state }, { moduleType, moduleId, item, sendboxTitle: title, snackbarText: text }) {
    commit('markAsFavorite', item);
    await dispatch('performSendboxItem2Action', {
      subject: item,
      moduleType,
      moduleId,
      action: 'ADD_FAVORITE',
      title,
      text,
      showInSendbox: true,
      writeAuditLog: false,
    });
  },
  async removeFromFavoriteMedia({ commit, dispatch, rootState, state }, { moduleType, moduleId, item, sendboxTitle: title, snackbarText: text }) {
    commit('removeFromFavorite', item);
    await dispatch('performSendboxItem2Action', {
      subject: item,
      moduleType,
      moduleId,
      action: 'REMOVE_FAVORITE',
      title,
      text,
      showInSendbox: true,
      writeAuditLog: false,
    });
  },
  async markAsRead2({ commit, dispatch, rootState, state }, { item, sendboxTitle: title, snackbarText: text, immediate }) {
    console.debug('markAsRead2', item);
    if (!item) return;

    if (mandatoryToReadService.isMandatoryToReadAndNotRead(item)) {
      if (!immediate) await delay(3000);
      mandatoryToReadService.markAsRead({ moduleId: item._moduleId, item });
      await dispatch('performSendboxItem2Action', {
        subject: item,
        action: 'SET_READ_STATE',
        title,
        text,
        showInSendbox: false,
      });
    } else if (mandatoryToReadService.isMandatoryToRead(item)) {
      // adapt read date for history, do not display in sendbox
      if (!immediate) await delay(3000);

      mandatoryToReadService.markAsRead({ moduleId: item._moduleId, item });

      await dispatch('performSendboxItem2Action', {
        subject: item,
        action: 'SET_READ_STATE',
        showInSendbox: false,
      });
    } else if (acknowledgeService.isToAcknowledge(item)) {
      console.debug("received 'markAsRead' but is to acknowledge. Do not mark right now");
      // }
    } else {
      if (!immediate) await delay(3000);

      let isUnread = readService.isUnread(item);

      if (isUnread) {
        readService.markAsRead(item);
        eventBus.publish({
          eventType: eventBus.createReadStateEventType(item),
          eventObj: { msg: 'read state changed', item },
        });
        console.debug('performSendboxItem2Action for unread item title', title);
        await dispatch('performSendboxItem2Action', {
          subject: item,
          action: 'SET_READ_STATE',
          title,
          text,
          showInSendbox: true,
        });
      } else {
        // already read -> do not display snackbar=>title:undefined
        // adapt read date for history, do not display in sendbox
        await dispatch('performSendboxItem2Action', {
          subject: item,
          action: 'SET_READ_STATE',
          showInSendbox: false,
        });
      }
    }
    // else if (acknowledgeService.isToAcknowledge(item)) {
    //   console.debug("received 'markAsRead' but is to acknowledge. Do not mark right now");
    // }
  },
  async markAllAsRead2({ commit, dispatch, rootState, state }, { moduleId, items, sendboxTitle, snackbarText }) {
    console.debug('markAllAsRead2', items);
    let sendboxItems = items
      .filter((item) => {
        if (mandatoryToReadService.isMandatoryToReadAndNotRead(item)) {
          //ignore mandatoryToReadService.markAsRead({ moduleId: item._moduleId, item });
        } else if (readService.isUnread(item)) {
          readService.markAsRead(item);
          return true;
        } else if (acknowledgeService.isToAcknowledge(item)) {
          // ignore
        }
        return false;
      })
      .map((item) => {
        return createSendboxItem2({
          subject: item,
          userState: item._userState,
          action: 'SET_READ_STATE',
          writeAuditLog: true,
        });
      });
    if (sendboxItems.length > 0) {
      let userNotification = {
        show: true,
        showInSendbox: false,
        title: sendboxTitle,
        text: snackbarText.replace('%count%', sendboxItems.length),
      };
      await dispatch('addAndSendSendboxItems', {
        moduleId,
        sendboxItems: sendboxItems,
        notification: userNotification,
      });
    }
  },
  async markAsUnread2({ commit, dispatch }, { item, sendboxTitle: title, snackbarText: text }) {
    if (!item) return;

    if (mandatoryToReadService.isMandatoryToRead(item)) {
      mandatoryToReadService.markAsUnread({ moduleId: item._moduleId, item });
      await dispatch('performSendboxItem2Action', {
        subject: item,
        action: 'RESET_READ_STATE',
        title,
        text,
        showInSendbox: false,
      });
    } else {
      readService.markAsUnread(item);
      await dispatch('performSendboxItem2Action', { subject: item, action: 'RESET_READ_STATE', title, text });
    }
  },
  markAsAcknowledged2({ dispatch, commit }, { item, sendboxTitle: title, snackbarText: text }) {
    console.debug('sendboxStore markAsAcknowledged', item);
    if (!item) return;
    acknowledgeService.markAsAcknowledged(item);
    dispatch('performSendboxItem2Action', { subject: item, action: 'SET_ACKNOWLEDGED', title, text });
  },
  markAsFavorite2({ commit, dispatch }, { item, sendboxTitle, snackbarText = '' }) {
    if (!item) return;
    if (!sendboxTitle) {
      sendboxTitle = i18n.t('action.addFavorite.msg', { title: item.title });
    }
    console.debug('2. sendbox store: markAsFavorite2', item._userState);
    //favoritesService.markAsFavorite(item);
    commit('markAsFavorite', item);
    // commit('fahrdienst/markAsFavorite',item,{root:true})
    dispatch('performSendboxItem2Action', {
      subject: item,
      action: 'ADD_FAVORITE',
      title: sendboxTitle,
      text: snackbarText,
    });
  },

  removeFromFavorite2({ commit, dispatch }, { item, sendboxTitle, snackbarText = '' }) {
    console.debug('removeFromFavorite2', item);

    if (!item) return;
    if (!sendboxTitle) {
      sendboxTitle = i18n.t('action.removeFavorite.msg', { title: item.title });
    }
    //favoritesService.removeFromFavorites(item);
    // commit('fahrdienst/removeFromFavorite',item,{root:true})
    commit('removeFromFavorite', item);
    // item._userState.favorite=item._userState.favorite===1?0:1
    dispatch('performSendboxItem2Action', {
      subject: item,
      action: 'REMOVE_FAVORITE',
      title: sendboxTitle,
      text: snackbarText,
    });
  },

  /**
   * Performs a sendbox action, that is, a sendbox item is created and sent, user gets a snackbar notification and an entry in the sendbox.
   * Finally the counter action of the respective module is called.
   * If no title and no text is provided, no snackbar nor a sendbox item is being displayed
   * @property {Object} moduleId - the module id used to delegate the sendboxItem handling to the module controller
   * @property {Object} subject - the mutated item to send to the server
   * @property {Object} payload - any arbitrary additional content to send with and to be handled by the backend module controller
   * @property {Object} userState - the mutated item to send to the server
   * @property {boolean} showAsUserInfo - true indicates the user should be notified by this action, e.g. by displaying in snackbar or the like
   * @property {boolean} showInSendbox - true to make the item visible within the sendbox display
   * @property {string} action - the action name (used as identifier)
   * @property {string} title - display title snackbar and sendbox. If undefined the snackbar will not be displayed
   * @property {string} text - Display text in user notification (e.g. snackbar, but not in sendbox)
   */
  async performSendboxItem2Action(
    { commit, dispatch },
    { subject, moduleId, payload, userState, title, text, action, showAsUserInfo: show = true, showInSendbox = true, writeAuditLog = true }
  ) {
    if (!title) {
      show = false;
      showInSendbox = false;
    }
    let userNotification = { show, showInSendbox, title, text };
    if (subject._moduleId) moduleId = subject._moduleId;

    let sendboxItem = new SendboxItem2({
      moduleId,
      writeAuditLog,
      userNotification,
      subject,
      payload,
      userState,
      action,
    });
    console.debug('performSendboxItem2Action', sendboxItem);
    await dispatch('addAndSendSendboxItems', {
      moduleId,
      sendboxItems: [sendboxItem],
      notification: userNotification,
    });
  },

  /**
   * Add an array of type SendboxItem2 to the sendbox and initiate sending of the sendbox.
   * @param {String} moduleId The moduleId the items belong to
   * @param {Array.<SendboxItem2>} sendboxItems one or more SendboxItems
   * @param {Array.<SendboxItem2>} notification UserNotification object
   */
  async addAndSendSendboxItems({ commit, dispatch }, { moduleId, sendboxItems, notification }) {
    commit('pushSendboxItems', sendboxItems);
    await dispatch('sendSendbox');
    await dispatch('postProcessSendActions', { moduleId, sendboxItems, notification });
  },

  /**
   * Perform the following mandatory steps after performing of a sendbox action:
   * - count module items (for badge)
   * - update module application cache to reflect data changes in cache
   * - display user notification (snackbar)
   * - synchronize app to make sure the sendbox is being sent
   * @param {Array.SendboxItem2} sendboxItems SendboxItem2
   */
  postProcessSendActions({ rootState, dispatch, commit }, { moduleId, sendboxItems, notification }) {
    commit(`${moduleId}/countModuleItems`, {}, { root: true });
    // dispatch(`${moduleId}/updateModuleApplicationCache`, {}, { root: true });
    if (notification && notification.show) {
      commit('snackbar/displaySnackbar', { text: notification.text }, { root: true });
    }
  },

  /**
   * An instance of SendboxItem.js (or a subclass) is added to the sendbox (store backed by localStorage item "sendbox")
   * Finally a "sendSendbox" action is dispatched (will try to send items to server)
   * @param SendboxItem sendboxItem
   */
  async addAction({ commit, dispatch }, action) {
    commit('addAction', action);
    await dispatch('sendSendbox');
  },

  /**
   * Process all sent items, that is
   * 1) set the sentDateTime and
   * 2) append the sent sendbox items to the sentItems
   * 3) sort them by date and time and
   * 4) keep the x newest items
   * @param {object} sendbox
   * @param {date} sentDateTime
   */
  processSentSendbox({ state, rootState, commit }, { sendbox, sentDateTime }) {
    try {
      if (sendbox && Array.isArray(sendbox)) {
        let sendBoxNew = sendbox.filter((s) => s && s.status && s.status !== 200);
        let sendBoxSent = sendbox.filter((s) => (s && !s.status) || (s && s.status && s.status === 200));

        sendBoxSent.forEach((item) => (item.sent = sentDateTime));

        let sentItemsFromLocalStorage = localStorage ? localStorage.getItem('sendbox.sent') : null;
        let sentItems = sentItemsFromLocalStorage ? JSON.parse(sentItemsFromLocalStorage) : state.sendboxSent;

        const maxSentItems = orDefault(rootState.configs.features.sentSendboxItemCount, 20);

        let sentItemsNew = sentItems
          .concat(sendBoxSent)
          .sort((item1, item2) => item2.sent - item1.sent)
          .slice(0, maxSentItems); //use maxSentItems latest items

        commit('setSendboxAndHistory', { sentItems: sentItemsNew, sendbox: sendBoxNew });
      }
    } catch (err) {
      console.error('Failed to process Sent Senbox items', err);
    }
  },

  /**
   * Sends all sendbox items to the server.
   */
  async sendSendbox({ rootState, dispatch }) {
    let sendBoxLocalStorageItem = localStorage.getItem('sendbox');
    let sendbox = JSON.parse(sendBoxLocalStorageItem ? sendBoxLocalStorageItem : '[]');

    if (rootState && rootState.isConnected && rootState.isServerReachable && sendbox && Array.isArray(sendbox) && sendbox.length > 0) {
      try {
        let sentDateTime = Date.now();
        const response = await sendboxApi.storeSendbox(sendbox); // send all sendbox items to the server
        if (response && response.data && response.data.responseList && Array.isArray(response.data.responseList)) {
          for (const [index, responseItem] of response.data.responseList.entries()) {
            try {
              if (responseItem && responseItem.code && sendbox[index]) {
                sendbox[index].status = responseItem.code;
              }
            } catch (err) {
              console.error('Failed to set Sent Sendbox status.', err);
            }
          }
        }

        // if successful, clear all userStates
        dispatch('processSentSendbox', { sendbox, sentDateTime });
      } catch (err) {
        console.error('Failed to store sendbox (probably offline). Keep localStorage and do not process Sent Sendbox', err);
      }
    }
  },

  /**
   *
   * @param {*} param0
   * @param {item}
   * @param {userStateType}  e.g. 'documentsUserState' or 'newsUserState
   * @param {actionType}
   * @param {moduleId} The id of the module whose user state data is modified here
   *
   * @returns
   */

  async executeAction({ rootState, dispatch, commit }, { action, moduleId, displaySnackbar = true }) {
    console.debug('executeAction');
    await dispatch('addAction', action);
    commit(`${moduleId}/countModuleItems`, {}, { root: true });
    dispatch(`${moduleId}/updateModuleApplicationCache`, {}, { root: true });
    displaySnackbar && commit('snackbar/displaySnackbar', { action }, { root: true });
    if (rootState.isConnected) {
      console.debug('synchronize sendbox as we are online');
      dispatch('sync/synchronizeApp', {}, { root: true });
    }
  },
};

/**
 * Create Sendbox Action based on given parameters
 */
// export async function getAction({ item, userStateType, actionType, immediate }) {
//   const getParams = (userInfo) => ({
//     title: item.title,
//     instance: item.instance,
//     module: item._moduleTitle,
//     displayLocation: item._displayLocation,
//     objectType: userStateType,
//     sendboxItem: { ...item._userState, _type: userStateType },
//     userInfo,
//   });

//   switch (actionType) {
//     case 'markAsRead':
//       if (readService.isRead(item)) return null;
//       console.debug('---- sendbox markAsRead immediate=', immediate, item);
//       if (!immediate) await delay(3000);
//       readService.markAsRead(item);
//       return new MarkAsReadAction(getParams(i18n.t('action.read.msg', { object: item.title })));

//     case 'markAsUnread':
//       if (readService.isUnread(item)) return; // ignore if we should mark as unread although the item is not read)
//       readService.markAsUnread(item);
//       return new MarkAsUnreadAction(getParams(i18n.t('action.unread.msg', { object: item.title })));

//     case 'markAsAcknowledged':
//       if (!immediate) await delay(3000);
//       acknowledgeService.markAsAcknowledged(item);
//       return new MarkAsAcknowledgedAction(getParams(i18n.t('action.acknowledge.msg', { object: item.title })));

//     case 'markAsFavorite': {
//       favoritesService.markAsFavorite(item);
//       return new AddFavoriteAction(getParams(i18n.t('action.addFavorite.msg', { title: item.title })));
//     }
//     case 'removeFromFavorites':
//       favoritesService.removeFromFavorites(item);
//       return new RemoveFavoriteAction(getParams(i18n.t('action.removeFavorite.msg', { title: item.title })));
//   }
//   console.debug('getAction did not find appropriate action for actionType', actionType);
// }

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
};
